import { Dialog } from "@dash/dialog";
import { Button, TextField } from "@dash/form";
import { Select } from "@dash/select";
import { useToast } from "@dash/toast";
import { useContext, useEffect, useMemo, useState } from "react";

import { IamAuthContext } from "@megaron/auth-react";
import { useClientManager } from "@megaron/react-clients";
import { Serializers } from "@megaron/serializers";
import { TicketDetailDto } from "@megaron/support-contracts";

import { AttachmentEntry, SupportAttachmentPicker } from "@megarax/crm-customers";
import { useTicketContactOptions } from "./useTicketContactOptions";

type Props = {
  ticket: TicketDetailDto;
  isOpen: boolean;
  onClose: () => void;
  onCreated: () => void;
};

export const SupportCreateTicketDialog: React.FC<Props> = ({ ticket, isOpen, onClose, onCreated }) => {
  const toast = useToast();
  const { iamUser } = useContext(IamAuthContext);
  const support = useClientManager("support");

  const [subject, setSubject] = useState("");
  const [message, setMessage] = useState("");
  const [sourceMailbox, setSourceMailbox] = useState("");
  const [attachments, setAttachments] = useState<AttachmentEntry[]>([]);

  const mailboxStatusQuery = support.imapConnectionStatus().useQuery({}, { enabled: isOpen });
  const mailboxOptions = useMemo(
    () => (mailboxStatusQuery.data?.mailboxes ?? []).map((m) => ({ label: m.email, value: m.email })),
    [mailboxStatusQuery.data?.mailboxes],
  );

  const { contactOptions, reporterEmailOptions, selectedContact, reporterEmail, reporterName, onContactChange, onReporterEmailChange } =
    useTicketContactOptions({ ticket, isOpen });

  useEffect(() => {
    if (!isOpen) {
      setSubject("");
      setMessage("");
      setSourceMailbox("");
      setAttachments([]);
    }
  }, [isOpen]);

  const createTicketMutation = support.createTicket().useMutation({
    onSuccess: () => {
      onCreated();
      toast.info("Utworzono zgłoszenie");
      onClose();
    },
    onError: () => {
      toast.error("Nie udało się utworzyć zgłoszenia");
    },
  });
  const isBusy = "isLoading" in createTicketMutation ? createTicketMutation.isLoading : false;

  const onCreate = () => {
    const trimmedSubject = subject.trim();
    const trimmedReporterEmail = reporterEmail.trim();
    const trimmedSourceMailbox = sourceMailbox.trim();
    const trimmedMessage = message.trim();
    if (!trimmedSubject || !trimmedReporterEmail || !trimmedMessage) {
      toast.error("Uzupełnij temat, wiadomość i kontakt");
      return;
    }
    const reporterEmailResult = Serializers.email.deserialize(trimmedReporterEmail);
    if (reporterEmailResult.isFailure) {
      toast.error("Podaj poprawny email kontaktu");
      return;
    }
    createTicketMutation.mutate({
      subject: trimmedSubject,
      message: trimmedMessage,
      reporterEmail: reporterEmailResult.value,
      reporterName: reporterName.trim() || trimmedReporterEmail.split("@")[0],
      sourceMailbox: trimmedSourceMailbox || null,
      status: "open",
      priority: null,
      tags: null,
      assigneeEmails: iamUser?.email ? [iamUser.email] : null,
      attachments: attachments.length > 0 ? attachments : null,
    });
  };

  if (!isOpen) return null;

  return (
    <Dialog onClose={isBusy ? undefined : onClose} header="Nowe zgłoszenie" placement="center" css={{ width: "420px" }}>
      <div css={{ display: "flex", flexDirection: "column", gap: "0.75rem" }}>
        <TextField
          label="Temat"
          value={subject}
          onChange={setSubject}
          css={{ width: "100%", input: { height: "46px" }, padding: 0 }}
          autoFocus
        />
        <Select
          label="Skrzynka odpowiedzi"
          value={sourceMailbox}
          onChange={(value) => setSourceMailbox(value ?? "")}
          options={mailboxOptions}
          isDisabled={mailboxStatusQuery.isLoading}
          allowEmptySelection
          placeholder="Bez skrzynki (tylko w aplikacji)"
        />
        <Select
          label="Email kontaktu"
          value={reporterEmail}
          onChange={(value) => onReporterEmailChange(value ?? "")}
          options={reporterEmailOptions}
          isDisabled={reporterEmailOptions.length === 0}
        />
        <Select
          label="Kontakt"
          value={selectedContact}
          onChange={(value) => onContactChange(value ?? "")}
          options={contactOptions.map((o) => ({ label: o.label, value: o.value }))}
          isDisabled={contactOptions.length === 0}
        />
        <TextField
          label="Wiadomość"
          value={message}
          onChange={setMessage}
          multiline
          css={{ width: "100%", textarea: { minHeight: "110px", resize: "vertical" }, padding: 0 }}
        />
        <SupportAttachmentPicker attachments={attachments} onChange={setAttachments} />
      </div>
      <div css={{ display: "flex", justifyContent: "flex-end", marginTop: "1rem" }}>
        <Button css={{ width: "auto", padding: "0.5rem 1rem" }} onClick={onCreate} isLoading={!!isBusy}>
          Utwórz
        </Button>
      </div>
    </Dialog>
  );
};
